import type { SiteRead } from "@/types/tina";

/** First non-empty trimmed string from a config value that may be a string or a list. */
function firstString(value: unknown): string {
  if (typeof value === "string") return value.trim();
  if (Array.isArray(value)) {
    const first = value.find(
      (v): v is string => typeof v === "string" && v.trim().length > 0,
    );
    return first ? first.trim() : "";
  }
  return "";
}

/**
 * Best human-openable source URL for a site, derived from its `config`. Covers
 * every strategy:
 *  - web (simple/api/sitemap) → `url` (first entry when it's a list) or `base_url`
 *  - facebook (facebook_group) → `url` (the full Facebook group URL)
 *  - instagram / legacy → the Instagram profile for `instagram_handle`
 *
 * A templated `url` (containing `{...}` placeholders, e.g. `?page={page}`) is
 * not directly openable, so it falls back to `base_url` when one exists.
 * Returns null when the config carries no usable URL.
 */
export function siteSourceUrl(site: SiteRead): string | null {
  const config = site.config ?? {};

  const url = firstString(config["url"]);
  const baseUrl = firstString(config["base_url"]);

  if (url && url.includes("{") && baseUrl) return baseUrl;
  if (url) return url;
  if (baseUrl) return baseUrl;

  const handle = firstString(config["instagram_handle"]);
  if (handle) return `https://www.instagram.com/${handle}/`;

  return null;
}
